import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import {
  AppBar,
  Box,
  Button,
  Card,
  Dialog,
  IconButton,
  Slide,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  LocationOnOutlined as LocationIcon,
  PersonOutline as PersonIcon,
  AccessTime as ClockIcon,
  CheckCircleOutline as CompleteIcon,
  RestartAltOutlined as RevertIcon,
} from '@mui/icons-material';
import ConfirmDialog from 'Components/ConfirmDialog';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { SESSION_PERMISSIONS, useHasPermission } from 'Utils/permissions';
import sessionsApi from 'Api/axios/sessions-api';
import { downloadFile } from 'Utils/file-utils';
import SessionStatusPill from './SessionStatusPill';
import AttendanceTable from './AttendanceTable';
import AttendanceBulkBar from './AttendanceBulkBar';
import AddAttendeeDialog from './AddAttendeeDialog';
import SessionSummaryCard from './SessionSummaryCard';
import EvidencesGrid from './EvidencesGrid';
import EvidenceUploader from './EvidenceUploader';
import { formatSessionDate, formatSessionTime } from '../utils/session-datetime';
import { isImmutableSessionError } from '../utils/session-immutable';
import useSessionDetailApi from '../Hooks/useSessionDetailApi';
import useAttendanceApi from '../Hooks/useAttendanceApi';
import useEvidencesApi from '../Hooks/useEvidencesApi';
import useSessionStatsApi from '../Hooks/useSessionStatsApi';

const SlideTransition = forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const styles = {
  appBar: { position: 'relative', bgcolor: colors.GREEN, boxShadow: 'none' },
  title: { ml: 2, flex: 1, fontSize: 16, fontWeight: 600 },
  // Extra bottom padding reserves space for the floating AttendanceBulkBar
  // (position: fixed at bottom 16) so the table pagination footer and the
  // last-row action buttons never sit under it.
  body: { p: { xs: 2, md: 4 }, pb: { xs: 14, md: 14 }, maxWidth: 1280, mx: 'auto' },
  headerCard: { p: 3, borderRadius: 2, mb: 2.5, border: `1px solid ${colors.GRAY_HR}`, boxShadow: 'none' },
  layout: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
    gap: 2.5,
  },
  meta: { display: 'inline-flex', alignItems: 'center', gap: 0.75, color: colors.GRAY_DARKER, fontSize: 13 },
  metaIcon: { fontSize: 16, color: colors.GRAY_DARK },
};

export default function SessionDetailDialog({ open, sessionId, onClose, onChanged, onEdit, onDelete }) {
  const { t2 } = useTranslation('EditCourseSessions');
  const canUpdate = useHasPermission(SESSION_PERMISSIONS.UPDATE);
  const canDelete = useHasPermission(SESSION_PERMISSIONS.DELETE);
  const canAttendanceUpdate = useHasPermission(SESSION_PERMISSIONS.ATTENDANCE_UPDATE);

  const { session, fetchSession, markComplete, unmarkComplete } = useSessionDetailApi({
    sessionId,
    init: false,
  });

  const {
    attendances,
    pagination: attendancePagination,
    loading: loadingAttendance,
    fetchAttendances,
    addAttendee,
    addAttendeesBulk,
    updateAttendance,
    bulkSave,
    removeAttendee,
  } = useAttendanceApi({ sessionId, init: false });

  const { evidences, uploadProgress, fetchEvidences, uploadEvidence, deleteEvidence } = useEvidencesApi({
    sessionId,
    init: false,
  });

  const { stats, loading: loadingStats, fetchStats } = useSessionStatsApi({ sessionId, init: false });

  // Mutations are blocked while the session is manually completed. The escape
  // hatch is the "Revertir" toggle in the AppBar (uncomplete is allowed).
  const isImmutable = !!session?.isManuallyCompleted;

  // Centralised error reporting so every mutation handler treats the
  // backend's `session_completed_immutable` code consistently — it shows a
  // toast with an inline action that runs `unmarkComplete` and re-tries.
  const reportMutationError = useCallback(
    (response, retry) => {
      if (isImmutableSessionError(response)) {
        toast.error(response.error || t2('immutable.lockedToast'), {
          action: {
            label: t2('immutable.revertAndEditAction'),
            onClick: async () => {
              if (!session) return;
              const revert = await unmarkComplete(session.id);
              if (revert.error) {
                toast.error(revert.error);
                return;
              }
              fetchSession();
              if (retry) await retry();
            },
          },
        });
      } else {
        toast.error(response.error);
      }
    },
    [session, unmarkComplete, fetchSession, t2]
  );

  const [filter, setFilter] = useState('all');
  // Search input is the live, controlled value; `debouncedSearch` is what we
  // actually send to the backend, throttled to avoid a request per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Server-side pagination state. Backend caps `limit` at 100; the UI exposes
  // 10/25/50/100. Page is 1-indexed to match the backend; MUI's TablePagination
  // is 0-indexed and translated in the table.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addAllSubmitting, setAddAllSubmitting] = useState(false);
  const [completionSubmitting, setCompletionSubmitting] = useState(false);
  // Single source of truth for the confirmation modal: { kind, payload } or null.
  // Discriminator avoids one boolean per confirmable action.
  const [confirmState, setConfirmState] = useState(null);
  const canEvidenceUpload = useHasPermission(SESSION_PERMISSIONS.EVIDENCE_UPLOAD);
  const canEvidenceDelete = useHasPermission(SESSION_PERMISSIONS.EVIDENCE_DELETE);

  // Fetch session detail + evidences + stats when the dialog opens or the target
  // changes; also reset the bulk selection so it never leaks between sessions.
  useEffect(() => {
    if (open && sessionId) {
      fetchSession();
      fetchEvidences();
      fetchStats();
      setSelectedIds(new Set());
    }
  }, [open, sessionId, fetchSession, fetchEvidences, fetchStats]);

  // Debounce the live search input (~300ms). The fetch effect below only
  // reacts to the debounced value, so typing doesn't trigger a request per key.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Fetch attendance list when the dialog opens, target changes, or pagination
  // / filter / search changes. All filters are server-driven — backend returns
  // the page slice, frontend renders.
  useEffect(() => {
    if (open && sessionId) {
      fetchAttendances({ status: filter, page, limit: pageSize, search: debouncedSearch });
    }
  }, [open, sessionId, filter, page, pageSize, debouncedSearch, fetchAttendances]);

  // Filter and page-size changes can leave us on an out-of-range page, so the
  // setters below always snap back to page 1. Selection is also cleared since
  // it lives in user-id space and the row set is about to change.
  const handleFilterChange = useCallback(next => {
    setFilter(next);
    setPage(1);
    setSelectedIds(new Set());
  }, []);

  const handlePageSizeChange = useCallback(next => {
    setPageSize(next);
    setPage(1);
  }, []);

  // Search changes can shrink the visible set, so always snap back to page 1
  // and drop the previous selection (which lived in user-id space).
  const handleSearchChange = useCallback(next => {
    setSearch(next);
    setPage(1);
    setSelectedIds(new Set());
  }, []);

  const handleToggleSelect = useCallback(userId => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    // Page-additive: toggling "seleccionar todos" only affects the rows
    // currently visible on this page. Other pages' selections are preserved
    // so the user can build a multi-page selection.
    setSelectedIds(prev => {
      const visibleIds = attendances.map(a => a.userId);
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach(id => {
          next.delete(id);
        });
      } else {
        visibleIds.forEach(id => {
          next.add(id);
        });
      }
      return next;
    });
  }, [attendances]);

  const handleUpdateStatus = useCallback(
    async (attendance, status) => {
      const run = async () => updateAttendance(attendance.userId, { status, comment: attendance.comment ?? null });
      const response = await run();
      if (response.error) {
        reportMutationError(response, run);
        return;
      }
      toast.success(t2('toasts.attendanceSaved'));
      fetchAttendances({ status: filter, page, limit: pageSize, search: debouncedSearch });
      fetchStats();
      if (onChanged) onChanged();
    },
    [
      updateAttendance,
      fetchAttendances,
      fetchStats,
      filter,
      page,
      pageSize,
      debouncedSearch,
      t2,
      onChanged,
      reportMutationError,
    ]
  );

  const handleUpdateComment = useCallback(
    async (attendance, draft) => {
      if (!attendance.status) return; // backend requires a status to PATCH; skip if still pending
      const run = async () =>
        updateAttendance(attendance.userId, { status: attendance.status, comment: draft || null });
      const response = await run();
      if (response.error) {
        reportMutationError(response, run);
        return;
      }
      fetchAttendances({ status: filter, page, limit: pageSize, search: debouncedSearch });
      fetchStats();
    },
    [updateAttendance, fetchAttendances, fetchStats, filter, page, pageSize, debouncedSearch, reportMutationError]
  );

  const handleRemove = useCallback(attendance => setConfirmState({ kind: 'removeAttendee', payload: attendance }), []);

  const handleAddAttendee = useCallback(
    async users => {
      if (!Array.isArray(users) || users.length === 0) return;
      const userIds = users.map(u => u.id);
      const run = async () => addAttendeesBulk(userIds);
      setAddSubmitting(true);
      const response = await run();
      setAddSubmitting(false);
      if (response.error) {
        reportMutationError(response, run);
        return;
      }
      const added = response.data?.added ?? 0;
      const enrolled = response.data?.enrolled ?? 0;
      toast.success(t2('toasts.attendeesAdded', [String(added)]));
      if (enrolled > 0) toast.success(t2('toasts.attendeesEnrolled', [String(enrolled)]));
      setAddOpen(false);
      fetchAttendances({ status: filter, page, limit: pageSize, search: debouncedSearch });
      fetchStats();
      if (onChanged) onChanged();
    },
    [
      addAttendeesBulk,
      fetchAttendances,
      fetchStats,
      filter,
      page,
      pageSize,
      debouncedSearch,
      t2,
      onChanged,
      reportMutationError,
    ]
  );

  const handleAddAllFromCourse = useCallback(async () => {
    if (!sessionId || addAllSubmitting) return;
    const run = async () => sessionsApi.addAllFromCourse(sessionId);
    setAddAllSubmitting(true);
    const response = await run();
    setAddAllSubmitting(false);
    if (response.error) {
      reportMutationError(response, run);
      return;
    }
    const added = response.data?.added ?? 0;
    toast.success(t2('toasts.attendeesAddedFromCourse', [String(added)]));
    fetchAttendances({ status: filter, page, limit: pageSize, search: debouncedSearch });
    fetchStats();
    if (onChanged) onChanged();
  }, [
    sessionId,
    addAllSubmitting,
    fetchAttendances,
    fetchStats,
    filter,
    page,
    pageSize,
    debouncedSearch,
    t2,
    onChanged,
    reportMutationError,
  ]);

  const handleBulk = useCallback(
    async status => {
      if (selectedIds.size === 0) return;
      const payload = {
        attendances: Array.from(selectedIds).map(userId => {
          const current = attendances.find(a => a.userId === userId);
          return {
            userId,
            status,
            comment: current?.comment ?? null,
          };
        }),
      };
      const run = async () => bulkSave(payload);
      setBulkSubmitting(true);
      const response = await run();
      setBulkSubmitting(false);
      if (response.error) {
        reportMutationError(response, run);
        return;
      }
      toast.success(t2('toasts.attendanceBulkSaved', [selectedIds.size]));
      setSelectedIds(new Set());
      fetchAttendances({ status: filter, page, limit: pageSize, search: debouncedSearch });
      fetchStats();
      if (onChanged) onChanged();
    },
    [
      selectedIds,
      attendances,
      bulkSave,
      fetchAttendances,
      fetchStats,
      filter,
      page,
      pageSize,
      debouncedSearch,
      t2,
      onChanged,
      reportMutationError,
    ]
  );

  const handleToggleCompletion = useCallback(async () => {
    if (!session) return;
    setCompletionSubmitting(true);
    const response = session.isManuallyCompleted ? await unmarkComplete(session.id) : await markComplete(session.id);
    setCompletionSubmitting(false);
    if (response.error) {
      toast.error(response.error);
      return;
    }
    toast.success(
      session.isManuallyCompleted ? t2('toasts.sessionUnmarkedCompleted') : t2('toasts.sessionMarkedCompleted')
    );
    fetchSession();
    if (onChanged) onChanged();
  }, [session, markComplete, unmarkComplete, fetchSession, onChanged, t2]);

  const handleUploadEvidence = useCallback(
    async file => {
      const run = async () => uploadEvidence(file);
      const response = await run();
      if (response.error) {
        reportMutationError(response, run);
        return;
      }
      toast.success(t2('toasts.evidenceUploaded'));
      fetchEvidences();
    },
    [uploadEvidence, fetchEvidences, t2, reportMutationError]
  );

  const handleDeleteEvidence = useCallback(
    evidence => setConfirmState({ kind: 'deleteEvidence', payload: evidence }),
    []
  );

  const performRemoveAttendee = useCallback(
    async attendance => {
      const run = async () => removeAttendee(attendance.userId);
      const response = await run();
      if (response.error) {
        reportMutationError(response, run);
        return;
      }
      toast.success(t2('toasts.attendeeRemoved'));
      fetchAttendances({ status: filter, page, limit: pageSize, search: debouncedSearch });
      fetchStats();
      if (onChanged) onChanged();
    },
    [
      removeAttendee,
      fetchAttendances,
      fetchStats,
      filter,
      page,
      pageSize,
      debouncedSearch,
      t2,
      onChanged,
      reportMutationError,
    ]
  );

  const performDeleteEvidence = useCallback(
    async evidence => {
      const run = async () => deleteEvidence(evidence.id);
      const response = await run();
      if (response.error) {
        reportMutationError(response, run);
        return;
      }
      toast.success(t2('toasts.evidenceDeleted'));
      fetchEvidences();
    },
    [deleteEvidence, fetchEvidences, t2, reportMutationError]
  );

  const handleConfirm = useCallback(async () => {
    if (!confirmState) return;
    const current = confirmState;
    setConfirmState(null);
    if (current.kind === 'removeAttendee') await performRemoveAttendee(current.payload);
    else if (current.kind === 'deleteEvidence') await performDeleteEvidence(current.payload);
  }, [confirmState, performRemoveAttendee, performDeleteEvidence]);

  const confirmCopy = (() => {
    if (confirmState?.kind === 'removeAttendee') {
      return {
        title: t2('attendance.remove'),
        description: t2('attendance.removeConfirm'),
        confirmText: t2('attendance.remove'),
      };
    }
    if (confirmState?.kind === 'deleteEvidence') {
      return {
        title: t2('evidences.remove'),
        description: t2('evidences.removeConfirm'),
        confirmText: t2('evidences.remove'),
      };
    }
    return { title: '', description: '', confirmText: '' };
  })();

  const handleExport = useCallback(async () => {
    if (!sessionId) return;
    const response = await sessionsApi.exportAttendance(sessionId);
    if (response.error) {
      toast.error(response.error);
      return;
    }
    downloadFile(response.data, `asistencia-${sessionId}.xlsx`);
    toast.success(t2('toasts.attendanceExported'));
  }, [sessionId, t2]);

  const meta = session && {
    title: session.title,
    description: session.description,
    location: session.location,
    instructorLabel: session.instructor?.fullName || session.instructorName,
    status: session.status,
  };

  const dateDisplay = useMemo(() => {
    if (!session) return null;
    return {
      date: formatSessionDate(session.startDateTime),
      time: `${formatSessionTime(session.startDateTime)} – ${formatSessionTime(session.endDateTime)}`,
    };
  }, [session]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen TransitionComponent={SlideTransition}>
      <AppBar sx={styles.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={styles.title}>{t2('detail.title')}</Typography>
          {canUpdate && session && (
            <Button
              startIcon={session.isManuallyCompleted ? <RevertIcon /> : <CompleteIcon />}
              onClick={handleToggleCompletion}
              disabled={completionSubmitting}
              sx={{ color: '#fff', textTransform: 'none', mr: 1 }}
            >
              {session.isManuallyCompleted ? t2('detail.unmarkCompleted') : t2('detail.markCompleted')}
            </Button>
          )}
          {canUpdate && session && (
            <Tooltip title={isImmutable ? t2('immutable.editDisabledTooltip') : ''}>
              <span>
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => onEdit(session)}
                  disabled={isImmutable}
                  sx={{
                    color: '#fff',
                    textTransform: 'none',
                    mr: 1,
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' },
                  }}
                >
                  {t2('detail.edit')}
                </Button>
              </span>
            </Tooltip>
          )}
          {canDelete && session && (
            <Button
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(session)}
              sx={{ color: '#fff', textTransform: 'none' }}
            >
              {t2('detail.delete')}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={styles.body}>
        {meta && (
          <Card sx={styles.headerCard} elevation={0}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 600, color: colors.BLACK }}>{meta.title}</Typography>
              <SessionStatusPill status={meta.status} />
            </Stack>
            {meta.description && (
              <Typography sx={{ fontSize: 13.5, color: colors.GRAY_DARKER, mb: 2, maxWidth: 720 }}>
                {meta.description}
              </Typography>
            )}
            <Stack direction="row" gap={3} flexWrap="wrap">
              {dateDisplay && (
                <Box sx={styles.meta}>
                  <ClockIcon sx={styles.metaIcon} />
                  {dateDisplay.date} · {dateDisplay.time}
                </Box>
              )}
              {meta.location && (
                <Box sx={styles.meta}>
                  <LocationIcon sx={styles.metaIcon} />
                  {meta.location}
                </Box>
              )}
              {meta.instructorLabel && (
                <Box sx={styles.meta}>
                  <PersonIcon sx={styles.metaIcon} />
                  {meta.instructorLabel}
                </Box>
              )}
            </Stack>
          </Card>
        )}

        <Box sx={styles.layout}>
          <AttendanceTable
            attendances={attendances}
            loading={loadingAttendance}
            filter={filter}
            onFilterChange={handleFilterChange}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            onUpdateStatus={handleUpdateStatus}
            onUpdateComment={handleUpdateComment}
            onRemove={handleRemove}
            onAddAttendee={() => setAddOpen(true)}
            onAddAllFromCourse={handleAddAllFromCourse}
            addAllSubmitting={addAllSubmitting}
            isImmutable={isImmutable}
            onExport={handleExport}
            page={page}
            pageSize={pageSize}
            totalItems={attendancePagination?.totalItems ?? 0}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            search={search}
            onSearchChange={handleSearchChange}
          />

          <Stack gap={2.5}>
            <SessionSummaryCard stats={stats} loading={loadingStats} />

            <Card
              sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${colors.GRAY_HR}`, boxShadow: 'none' }}
              elevation={0}
            >
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: colors.GRAY_DARK,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  mb: 1.5,
                }}
              >
                {t2('detail.evidencesSection')}
              </Typography>
              {canEvidenceUpload && (
                <Box sx={{ mb: 1.5 }}>
                  <EvidenceUploader
                    onUpload={handleUploadEvidence}
                    currentCount={evidences.length}
                    uploadProgress={uploadProgress}
                    disabled={isImmutable}
                  />
                </Box>
              )}
              <EvidencesGrid
                evidences={evidences}
                canDelete={canEvidenceDelete && !isImmutable}
                onDelete={handleDeleteEvidence}
              />
            </Card>
          </Stack>
        </Box>
      </Box>

      {canAttendanceUpdate && !isImmutable && (
        <AttendanceBulkBar
          count={selectedIds.size}
          onMark={handleBulk}
          onClear={() => setSelectedIds(new Set())}
          disabled={bulkSubmitting}
        />
      )}

      <AddAttendeeDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onConfirm={handleAddAttendee}
        submitting={addSubmitting}
        courseId={session?.courseId}
      />

      <ConfirmDialog
        open={!!confirmState}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmText={confirmCopy.confirmText}
        cancelText={t2('form.cancel')}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmState(null)}
      />
    </Dialog>
  );
}

SessionDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  sessionId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

SessionDetailDialog.defaultProps = { sessionId: null, onChanged: null };
